/** The Submit an idea dialog's form, once the catalogue has loaded. */

import { Plus, X } from 'lucide-react';

import { ideaInDescriptionMax, ideaInSourceUrlsItemMax, ideaInSourceUrlsMax, ideaInTitleMax } from '@/api/generated/schemas';
import { bannerError } from '@/api/validate';
import { useSession } from '@/session/SessionContext';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { FieldLabel, InfoTip } from '@/ui/FieldLabel';
import { MarkdownEditor } from '@/ui/MarkdownEditor';
import { FormError } from '@/ui/StateCard';

import { TYPED_FIELDS, type SubmitIdeaForm } from './useSubmitIdeaForm';

function SourceUrlsField({ form }: { form: SubmitIdeaForm }) {
  const { sourceUrls, setUrl, removeUrl, addUrl, serverErrors } = form;
  // The body sends only the filled rows, so a 422's `source_urls.N` is the Nth filled row.
  const sent = sourceUrls.reduce<number[]>((at, url, i) => (url.trim() ? [...at, i] : at), []);
  const urlError = (index: number) => serverErrors[`source_urls.${sent.indexOf(index)}`];
  // A message about the whole list describes every row; a row's own marks that row invalid.
  const listHint = fieldA11y('idea-source', serverErrors.source_urls, 'idea-source-hint')['aria-describedby'];
  return (
    // Named by the legend's text alone: the tip's button and bubble sit in the legend, and would
    // otherwise join the group's accessible name.
    <fieldset className="field field-group" aria-labelledby="idea-source-label">
      <legend className="label">
        <span className="field-label">
          <span id="idea-source-label">Source URLs</span>
          <InfoTip id="idea-source-hint" about="Source URLs">
            {`Optional, up to ${ideaInSourceUrlsMax}. Given to research as context; they are not opened or fetched.`}
          </InfoTip>
        </span>
      </legend>
      <div className="url-list">
        {sourceUrls.map((url, index) => (
          <div key={index} className="url-row">
            <input
              id={`idea-source-${index}`}
              type="url"
              maxLength={ideaInSourceUrlsItemMax}
              value={url}
              placeholder="https://… (optional)"
              aria-label={`Source URL ${index + 1}`}
              {...fieldA11y(`idea-source-${index}`, urlError(index), listHint)}
              onChange={(e) => setUrl(index, e.target.value)}
            />
            {(sourceUrls.length > 1 || url !== '') && (
              <button type="button" className="icon-btn" aria-label={`Remove source URL ${index + 1}`} title="Remove" onClick={() => removeUrl(index)}>
                <X aria-hidden="true" />
              </button>
            )}
            <FieldError inputId={`idea-source-${index}`} message={urlError(index)} />
          </div>
        ))}
        {sourceUrls.length < ideaInSourceUrlsMax && (
          <button type="button" className="btn btn-ghost btn-sm url-add" onClick={addUrl}>
            <Plus aria-hidden="true" /> Add another URL
          </button>
        )}
      </div>
      <FieldError inputId="idea-source" message={serverErrors.source_urls} />
    </fieldset>
  );
}

function IndustryAndAuthor({ form }: { form: SubmitIdeaForm }) {
  const { user } = useSession();
  return (
    <div className="grid grid-2">
      <div className="field">
        <FieldLabel htmlFor="idea-industry" id="idea-industry-hint" label="Industry" hint="Optional. Leave it open and research suggests the best fit." />
        <select id="idea-industry" value={form.industryId} onChange={(e) => form.setIndustryId(e.target.value)} aria-describedby="idea-industry-hint">
          <option value="">Not sure / cross-industry</option>
          {form.industries.map((industry) => (
            <option key={industry.id} value={industry.id}>
              {industry.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="idea-author">Submitted by</label>
        <input id="idea-author" type="text" readOnly value={user?.display_name ?? ''} />
      </div>
    </div>
  );
}

/** The paths a 422 can name that sit beside a field here; any other keeps the banner. */
function shownPaths(urls: number): string[] {
  return [...TYPED_FIELDS, ...Array.from({ length: urls }, (_, i) => `source_urls.${i}`)];
}

export function SubmitIdeaFields({ form }: { form: SubmitIdeaForm }) {
  const { title, setTitle, description, setDescription } = form;
  const shown = shownPaths(form.sourceUrls.length);
  return (
    <div className="stack-md">
      <FormError error={bannerError(form.submit.error, shown)} />

      <div className="field">
        <label htmlFor="idea-title">Title</label>
        <input
          id="idea-title"
          type="text"
          required
          autoFocus
          maxLength={ideaInTitleMax}
          value={title}
          placeholder="e.g. Agentic KYC refresh for mid-tier banks"
          {...fieldA11y('idea-title', form.serverErrors.title)}
          onChange={(e) => setTitle(e.target.value)}
        />
        <FieldError inputId="idea-title" message={form.serverErrors.title} />
      </div>

      <IndustryAndAuthor form={form} />

      <div className="field">
        <label htmlFor="idea-description">Description</label>
        <MarkdownEditor
          id="idea-description"
          rows={8}
          maxLength={ideaInDescriptionMax}
          value={description}
          placeholder="The problem, who has it, what the solution could be, and why now. Markdown is supported."
          describedBy="idea-description-count"
          error={form.serverErrors.description}
          onChange={setDescription}
        />
        <span id="idea-description-count" className="hint char-count" aria-live="polite">
          {description.length.toLocaleString('en-US')} / {ideaInDescriptionMax.toLocaleString('en-US')} characters
        </span>
      </div>

      <SourceUrlsField form={form} />
    </div>
  );
}
